
/**
 * Converts degrees to radians
 */
export const deg2rad = (deg: number): number => {
  return deg * (Math.PI / 180);
};

/**
 * Calculates the distance between two points in km using the Haversine formula
 */
export const calculateDistance = (
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km
  return distance;
};

interface PartnerWithLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius?: number;
  leadCount?: number;
  successCount?: number;
  [key: string]: any;
}

/**
 * Finds the nearest partner to a given location
 */
export const findNearestPartner = (leadLat: number, leadLon: number): PartnerWithLocation | null => {
  const storedPartners = localStorage.getItem("partners");
  if (!storedPartners) return null;

  const partners = JSON.parse(storedPartners) as PartnerWithLocation[];
  if (!partners || partners.length === 0) return null;

  let nearestPartner: PartnerWithLocation | null = null;
  let minDistance = Infinity;

  for (const partner of partners) {
    if (partner.latitude && partner.longitude && partner.radius) {
      const distance = calculateDistance(leadLat, leadLon, partner.latitude, partner.longitude);
      if (distance <= (partner.radius || 10) && distance < minDistance) {
        nearestPartner = partner;
        minDistance = distance;
      }
    }
  }

  return nearestPartner;
};

/**
 * Get the next partner in a round-robin assignment
 * Assigns leads to partners in order, one by one, cycling through the list
 */
export const getNextRoundRobinPartner = (
  partners: PartnerWithLocation[],
  service: string,
  lastAssignedPartnerId?: string
): PartnerWithLocation | null => {
  // Filter active partners that provide the requested service
  const eligiblePartners = partners.filter(partner => 
    partner.active && 
    partner.services && 
    partner.services.includes(service)
  );

  if (eligiblePartners.length === 0) return null;
  if (eligiblePartners.length === 1) return eligiblePartners[0];

  // Sort partners by ID to ensure consistent ordering
  eligiblePartners.sort((a, b) => a.id.localeCompare(b.id));

  // Find the index of the last assigned partner
  const lastIndex = lastAssignedPartnerId 
    ? eligiblePartners.findIndex(p => p.id === lastAssignedPartnerId)
    : -1;

  // Select the next partner in sequence
  const nextIndex = (lastIndex + 1) % eligiblePartners.length;
  return eligiblePartners[nextIndex];
};

/**
 * Finds the most suitable partner for a lead based on fair distribution
 * Takes into account distance, current lead count, and success rate
 */
export const findFairestPartner = (
  leadLat: number, 
  leadLon: number, 
  service: string
): PartnerWithLocation | null => {
  const storedPartners = localStorage.getItem("partners");
  if (!storedPartners) return null;

  const partners = JSON.parse(storedPartners) as PartnerWithLocation[];
  if (!partners || partners.length === 0) return null;

  // Filter active partners that provide the requested service
  const eligiblePartners = partners.filter(partner => 
    partner.active && 
    partner.services && 
    partner.services.includes(service) && 
    partner.latitude && 
    partner.longitude && 
    partner.radius
  );

  if (eligiblePartners.length === 0) return null;

  // If there's only one eligible partner, assign to them
  if (eligiblePartners.length === 1) return eligiblePartners[0];

  // Find all partners within radius of the lead
  const partnersInRange = eligiblePartners.filter(partner => {
    const distance = calculateDistance(leadLat, leadLon, partner.latitude!, partner.longitude!);
    return distance <= (partner.radius || 10);
  });

  if (partnersInRange.length === 0) return null;
  if (partnersInRange.length === 1) return partnersInRange[0];

  // Calculate a score for each partner based on:
  // 1. Distance (closer is better)
  // 2. Lead count (fewer leads is better for fair distribution)
  // 3. Success rate (higher success rate is better)
  const partnersWithScores = partnersInRange.map(partner => {
    const distance = calculateDistance(leadLat, leadLon, partner.latitude!, partner.longitude!);
    const distanceScore = 1 - (distance / (partner.radius || 10)); // 1 when distance=0, 0 when distance=radius
    
    // Lead count score (inverse, so fewer leads = higher score)
    const leadCount = partner.leadCount || 0;
    const maxLeads = Math.max(...partnersInRange.map(p => p.leadCount || 0), 1);
    const leadCountScore = 1 - (leadCount / maxLeads);
    
    // Success rate score
    const successCount = partner.successCount || 0;
    const successRate = leadCount > 0 ? successCount / leadCount : 0;
    
    // Calculate combined score (weighted)
    // We prioritize fair distribution (leadCountScore) but also consider distance and success rate
    const score = (leadCountScore * 0.5) + (distanceScore * 0.3) + (successRate * 0.2);
    
    return {
      ...partner,
      score
    };
  });

  // Sort by score (highest first) and return the best match
  partnersWithScores.sort((a, b) => b.score - a.score);
  return partnersWithScores[0];
};
